import { Link, useLocation } from "react-router-dom";
import { useContext, useState, useEffect } from "react";
import { motion, AnimatePresence } from 'framer-motion';
import {
  HomeIcon,
  ShoppingCartIcon,
  Upload,
  CircleUserRound,
  HandHelping,
  ChevronDownIcon,
  LogOut,
  MenuIcon,
  XIcon,
  LayoutTemplate,
  Briefcase,
  Package,
  FileText,
  MapPin,
  Mail,
  Ticket,
} from "lucide-react";
import AuthContext from '../context/AuthContext';
import axios from 'axios';
import Swal from 'sweetalert2';
import logo_img from '../media/logo-img.png';
import Accordion from './ui/Accordion';
import Badge from './ui/Badge';
import API_BASE_URL from '../config/api';

export default function Sidebar() {
  const { user, authToken, logoutUser } = useContext(AuthContext);
  const userName = user?.username || "User";
  const [profileOpen, setProfileOpen] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState({
    is_active: false,
    expiry_date: null,
  });
  const location = useLocation();

  const authAxios = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      Authorization: `Bearer ${authToken?.access}`,
    },
  });

  const fetchSubscriptionStatus = async () => {
    if (!authToken) return;
    try {
      const response = await authAxios.get('/api/get_subscription_status/');
      setSubscriptionStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch subscription status:', err);
      setSubscriptionStatus({ is_active: false, expiry_date: null });
      if (err.response?.status === 401) {
        Swal.fire({
          title: 'Session Expired',
          text: 'Please login again.',
          icon: 'warning',
          confirmButtonColor: "#d45614",
        }).then(() => {
          logoutUser();
        });
      }
    }
  };

  useEffect(() => {
    fetchSubscriptionStatus();
  }, [authToken]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  }, [location.pathname]);

  const sidebarSharedClasses = "fixed inset-y-0 left-0 flex flex-col w-[260px] bg-white border-r border-[var(--color-gray-200)] shadow-[var(--shadow-lg)] transform transition-all duration-300 ease-in-out font-fell";
  const mobileHiddenClasses = "-translate-x-full";
  const mobileVisibleClasses = "translate-x-0";
  const desktopVisibleClasses = "lg:translate-x-0 lg:shadow-[var(--shadow-sm)] lg:border-[var(--color-gray-100)]";

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  const toggleButtonBaseClasses = "lg:hidden fixed top-4 p-2 bg-white rounded-[var(--radius-full)] text-[var(--color-gray-700)] hover:bg-[var(--color-gray-100)] focus:outline-none focus:ring-2 focus:ring-[var(--color-gray-400)] focus:ring-offset-1 transition-all duration-300 ease-in-out shadow-[var(--shadow-md)]";
  const toggleButtonClosedPosition = "left-4";
  const toggleButtonOpenPosition = "left-[200px] top-2 border-transparent shadow-none";

  const handleUploadClick = () => {
    Swal.fire({
        title: 'Subscription Required',
        text: 'Please subscribe to add designs to your store.',
        icon: 'error',
        confirmButtonColor: "#d45614",
    });
  };

  return (
    <>
      <motion.button
        onClick={(e) => {
          e.stopPropagation();
          toggleMobileMenu();
        }}
        className={`${toggleButtonBaseClasses} ${isMobileMenuOpen ? toggleButtonOpenPosition : toggleButtonClosedPosition} touch-manipulation`}
        style={{
          zIndex: 'var(--z-sidebar-toggle)',
          minHeight: '44px',
          minWidth: '44px',
          pointerEvents: 'auto'
        }}
        aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
        aria-expanded={isMobileMenuOpen}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        {isMobileMenuOpen ? <XIcon size={24} /> : <MenuIcon size={24} />}
      </motion.button>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="fixed inset-0 bg-black/40 lg:hidden touch-manipulation"
            style={{
              zIndex: 'var(--z-modal-backdrop)',
              pointerEvents: 'auto'
            }}
            onClick={(e) => {
              e.stopPropagation();
              setIsMobileMenuOpen(false);
            }}
            onTouchEnd={(e) => {
              e.stopPropagation();
              setIsMobileMenuOpen(false);
            }}
            aria-hidden="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          />
        )}
      </AnimatePresence>

      <motion.aside
        className={`lg:w-[270px] ${sidebarSharedClasses} ${isMobileMenuOpen ? mobileVisibleClasses : mobileHiddenClasses} ${desktopVisibleClasses}`}
        style={{ zIndex: 'var(--z-modal)', borderRadius: 0 }}
        aria-label="Sidebar"
        initial={false}
        animate={isMobileMenuOpen ? "open" : "closed"}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b border-[var(--color-gray-200)] shrink-0" style={{ borderRadius: 0 }}>
          <Link to="/homepage" className="flex items-center" onClick={() => isMobileMenuOpen && setIsMobileMenuOpen(false)}>
            <img
              src={'https://d3qvi06vol9hrb.cloudfront.net/static/media/logo-img.e08e272c66f1e35ac87c.png'} 
              alt="Logo"
              className="h-8 w-auto object-contain"
              style={{ borderRadius: 0 }}
            />
          </Link>
        </div>

        <nav className="flex-grow space-y-1.5 p-4 overflow-y-auto custom-scrollbar" style={{ borderRadius: 0 }}>
          <NavItem
            icon={<HomeIcon size={20} />}
            label="My Store"
            to="/homepage"
            currentPath={location.pathname}
            closeMobileMenu={() => isMobileMenuOpen && setIsMobileMenuOpen(false)}
          />
          <NavItem
            icon={<Upload size={20} />}
            label="Add New Design"
            to="/UploadProduct"
            currentPath={location.pathname}
            closeMobileMenu={() => isMobileMenuOpen && setIsMobileMenuOpen(false)}
            disabled={!subscriptionStatus.is_active}
            onDisabledClick={handleUploadClick}
            badge={!subscriptionStatus.is_active ? <Badge variant="warning" size="sm">Pro</Badge> : null}
          />
          <NavItem
            icon={<ShoppingCartIcon size={20} />}
            label="Orders"
            to="/Orders"
            currentPath={location.pathname}
            closeMobileMenu={() => isMobileMenuOpen && setIsMobileMenuOpen(false)}
          />
          <NavItem
            icon={<Ticket size={20} />}
            label="My Coupons"
            to="/my-coupons"
            currentPath={location.pathname}
            closeMobileMenu={() => isMobileMenuOpen && setIsMobileMenuOpen(false)}
          />
          <NavItem
            icon={<HandHelping size={20} />}
            label="Custom Requests"
            to="/Proposals"
            currentPath={location.pathname}
            closeMobileMenu={() => isMobileMenuOpen && setIsMobileMenuOpen(false)}
          />
          <NavItem
            icon={<Briefcase size={20} />}
            label="Designer KYC"
            to="/designer-onboarding"
            currentPath={location.pathname}
            closeMobileMenu={() => isMobileMenuOpen && setIsMobileMenuOpen(false)}
          />

          <div className="pt-2">
            <button
              onClick={() => setProfileOpen(!profileOpen)}
              aria-expanded={profileOpen}
              aria-controls="profile-submenu"
              className="flex items-center justify-between w-full text-left px-3 py-2.5 rounded-[var(--radius-md)] hover:bg-[var(--color-gray-100)] focus:outline-none focus:ring-2 focus:ring-[var(--color-gray-300)] focus:ring-offset-1 focus:bg-[var(--color-gray-100)] transition-all duration-200 group"
            >
              <span className="flex items-center space-x-3">
                <CircleUserRound size={22} className="text-[var(--color-gray-500)] group-hover:text-[var(--color-gray-700)] transition-colors" />
                <span className="font-medium text-base text-[var(--color-gray-800)] group-hover:text-[var(--color-gray-900)] transition-colors">{userName}</span>
              </span>
              <motion.div
                animate={{ rotate: profileOpen ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <ChevronDownIcon
                  className="text-[var(--color-gray-400)] group-hover:text-[var(--color-gray-600)]"
                  size={20}
                />
              </motion.div>
            </button>
            <AnimatePresence initial={false}>
              {profileOpen && (
                <motion.div
                  id="profile-submenu"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className="overflow-hidden"
                >
                  <div className="pl-[calc(22px+0.75rem)] pr-1 space-y-1.5 pt-2">
                    <SubNavItem
                      icon={<CircleUserRound size={16} />}
                      label="Brand Settings"
                      to="/Profile"
                      currentPath={location.pathname}
                      closeMobileMenu={() => isMobileMenuOpen && setIsMobileMenuOpen(false)}
                    />
                    <SubNavItem
                      icon={<Briefcase size={16} />}
                      label="Designer Profile"
                      to="/designer-profile"
                      currentPath={location.pathname}
                      closeMobileMenu={() => isMobileMenuOpen && setIsMobileMenuOpen(false)}
                    />
                    <SubNavItem
                      icon={<LayoutTemplate size={16} />}
                      label="Themes"
                      to="/TemplateThemes"
                      currentPath={location.pathname}
                      closeMobileMenu={() => isMobileMenuOpen && setIsMobileMenuOpen(false)}
                    />
                    <SubNavItem
                      icon={<Package size={16} />}
                      label="Manage Subscription"
                      to="/subscription"
                      currentPath={location.pathname}
                      closeMobileMenu={() => isMobileMenuOpen && setIsMobileMenuOpen(false)}
                    />
                    <SubNavItem
                      icon={<MapPin size={16} />}
                      label="Manage Address"
                      to="/ManageAddress"
                      currentPath={location.pathname}
                      closeMobileMenu={() => isMobileMenuOpen && setIsMobileMenuOpen(false)}
                    />
                    <SubNavItem
                      icon={<Mail size={16} />}
                      label="Contact Us"
                      to="/Contact"
                      currentPath={location.pathname}
                      closeMobileMenu={() => isMobileMenuOpen && setIsMobileMenuOpen(false)}
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </nav>

        <div className="p-4 border-t border-[var(--color-gray-200)] shrink-0">
          <button
            onClick={() => {
              logoutUser();
              if (isMobileMenuOpen) setIsMobileMenuOpen(false);
            }}
            className="flex items-center space-x-3 w-full px-3 py-2.5 rounded-[var(--radius-md)] text-[var(--color-gray-600)] hover:bg-[var(--color-error-light)] hover:text-[var(--color-error)] focus:outline-none focus:ring-2 focus:ring-[var(--color-error-dark)] focus:ring-offset-1 focus:bg-[var(--color-error-light)] focus:text-[var(--color-error)] transition-all duration-200 group"
          >
            <LogOut size={20} className="text-[var(--color-gray-500)] group-hover:text-[var(--color-error)] transition-colors" />
            <span className="text-sm font-medium">Log out</span>
          </button>
        </div>
      </motion.aside>
    </>
  );
}

function NavItem({ icon, label, to, currentPath, closeMobileMenu, disabled, onDisabledClick, badge }) {
  const isActive = currentPath === to;

  const handleClick = (e) => {
    if (disabled) {
      e.preventDefault();
      if (onDisabledClick) {
        onDisabledClick();
      }
    } else {
      if (closeMobileMenu) closeMobileMenu();
    }
  };

  return (
    <Link
      to={to}
      onClick={handleClick}
      aria-current={isActive ? "page" : undefined}
      className={`flex items-center justify-between px-3 py-2.5 rounded-[var(--radius-md)] transition-all duration-200 group focus:outline-none ${
        isActive
          ? 'bg-[var(--color-accent-light)] text-[var(--color-primary)] shadow-[var(--shadow-sm)] hover:" style={{ backgroundColor: COLORS.AUTUMN }} className=" focus:ring-2 focus:ring-[var(--color-accent-dark)] focus:ring-offset-1'
          : `text-[var(--color-gray-700)] hover:bg-[var(--color-gray-100)] hover:text-[var(--color-gray-900)] focus:bg-[var(--color-gray-100)] focus:ring-2 focus:ring-[var(--color-gray-300)] focus:ring-offset-1`
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <div className="flex items-center space-x-3">
        <span className={`transition-colors duration-200 ${
          isActive
            ? 'text-[var(--color-primary)]'
            : 'text-[var(--color-gray-400)] group-hover:text-[var(--color-gray-600)]'
        }`}>
          {icon}
        </span>
        <span className={`text-base font-medium transition-colors duration-200 ${
            isActive ? 'text-[var(--color-primary)]' : 'text-[var(--color-gray-700)] group-hover:text-[var(--color-gray-900)]'
        }`}>
          {label}
        </span>
      </div>
      {badge && <span className="ml-auto">{badge}</span>}
    </Link>
  );
}

function SubNavItem({ icon, label, to, currentPath, closeMobileMenu }) {
  const isActive = currentPath === to;

  return (
    <Link
      to={to}
      onClick={closeMobileMenu}
      aria-current={isActive ? "page" : undefined}
      className={`block text-xs px-3 py-2 rounded-[var(--radius-md)] transition-all duration-200 flex items-center space-x-2 group focus:outline-none ${
        isActive
            ? 'bg-[var(--color-accent-light)] text-[var(--color-primary)] font-semibold hover:" style={{ backgroundColor: COLORS.AUTUMN }} className=" focus:ring-1 focus:ring-[var(--color-accent-dark)] focus:ring-offset-1'
            : 'text-[var(--color-gray-500)] hover:text-[var(--color-gray-800)] hover:bg-[var(--color-gray-100)] focus:bg-[var(--color-gray-100)] focus:ring-1 focus:ring-[var(--color-gray-300)] focus:ring-offset-1'
        }`}
      >
        {icon && (
            <span className={`transition-colors duration-200 ${
                isActive ? 'text-[var(--color-primary)]' : 'text-[var(--color-gray-400)] group-hover:text-[var(--color-gray-500)]'
            }`}>
                {icon}
            </span>
        )}
        <span className="text-base">{label}</span>
      </Link>
  );
}